import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import MetricCard from './components/MetricCard';

import ActivityItem from './components/ActivityItem';
import PerformanceChart from './components/PerformanceChart';
import CategoryPerformanceCard from './components/CategoryPerformanceCard';
import SystemSettingsCard from './components/SystemSettingsCard';
import { adminService } from '../../services/adminService';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalImages: 0,
    totalVideos: 0,
    totalCategories: 0
  });

  const performanceData = [
    { name: 'Jan', visualizações: 1240, avaliações: 320 },
    { name: 'Fev', visualizações: 1580, avaliações: 410 },
    { name: 'Mar', visualizações: 1920, avaliações: 520 },
    { name: 'Abr', visualizações: 2150, avaliações: 580 },
    { name: 'Mai', visualizações: 2480, avaliações: 640 },
    { name: 'Jun', visualizações: 2890, avaliações: 750 }
  ];

  const categoryPerformance = [
    { name: 'Artes de Filmes e Séries', itemCount: 145, views: '12.5k', rating: '4.8', trend: 'up' },
    { name: 'Artes do Big Brother Brasil', itemCount: 98, views: '9.8k', rating: '4.7', trend: 'up' },
    { name: 'Artes do Brasileirão', itemCount: 76, views: '8.2k', rating: '4.6', trend: 'stable' },
    { name: 'Artes de Parcerias de Aplicativos', itemCount: 54, views: '6.1k', rating: '4.5', trend: 'down' }
  ];

  const handleApprove = (id) => {
    console.log('Approved item:', id);
  };

  const handleReject = (id) => {
    console.log('Rejected item:', id);
  };

  const quickActions = [
    { icon: 'FolderOpen', label: 'Gerenciar Conteúdo', path: '/content-management', color: 'primary' },
    { icon: 'Grid', label: 'Categorias', path: '/category-management', color: 'secondary' },
    { icon: 'Package', label: 'Planos', path: '/plans-management', color: 'accent' },
    { icon: 'Award', label: 'Hall da Fama', path: '/hall-of-fame-editor', color: 'primary' },
    { icon: 'Users', label: 'Administradores', path: '/admin-users', color: 'secondary' },
    { icon: 'Settings', label: 'Configurações', path: '/admin-settings', color: 'primary' }
  ];

  // Load notifications from database
  useEffect(() => {
    loadNotifications();
    loadDashboardStats();
    
    // Subscribe to real-time notifications
    const unsubscribe = adminService?.subscribeToNotifications((newNotification) => {
      setNotifications(prev => [newNotification, ...prev]);
    });

    return () => unsubscribe();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await adminService?.getNotifications(50);
      setNotifications(data);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const data = await adminService?.getDashboardStats();
      setStats(data);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - UP MIDIA Gallery</title>
        <meta name="description" content="Painel administrativo completo para gerenciamento de conteúdo, métricas de desempenho e customização do sistema UP MIDIA Gallery" />
      </Helmet>
      <div className="min-h-screen bg-background pb-20">
        {error && (
          <div className="mx-4 mt-4 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Carregando dashboard...</p>
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
            <div className="mb-6 md:mb-8">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-primary mb-2">
                Dashboard Administrativo
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Visão geral completa do sistema UP MIDIA Gallery
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              <MetricCard
                title="Total de Artes"
                value={stats?.totalImages?.toString()}
                change="+12% este mês"
                changeType="positive"
                icon="Image"
                iconColor="var(--color-primary)"
              />
              <MetricCard
                title="Total de Vídeos"
                value={stats?.totalVideos?.toString()}
                change="+8% este mês"
                changeType="positive"
                icon="Video"
                iconColor="var(--color-secondary)"
              />
              <MetricCard
                title="Categorias Ativas"
                value={stats?.totalCategories?.toString()}
                change="Sem alterações"
                changeType="stable"
                icon="FolderOpen"
                iconColor="var(--color-accent)"
              />
              <MetricCard
                title="Engajamento Total"
                value="28.9k"
                change="+15% este mês"
                changeType="positive"
                icon="TrendingUp"
                iconColor="var(--color-success)"
              />
            </div>

            <section className="container mx-auto px-4 py-8">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
                {quickActions?.map((action, index) => (
                  <Link key={index} to={action?.path}>
                    <div className="bg-card rounded-lg p-4 flex flex-col items-center gap-3 hover:glow-primary-hover transition-smooth cursor-pointer">
                      <div className={`w-12 h-12 rounded-md bg-${action?.color}/10 flex items-center justify-center glow-${action?.color}`}>
                        <Icon name={action?.icon} size={24} color={`var(--color-${action?.color})`} />
                      </div>
                      <span className="text-sm font-medium text-foreground text-center">
                        {action?.label}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="lg:col-span-2">
                <PerformanceChart
                  data={performanceData}
                  title="Desempenho Mensal"
                />
              </div>

              <div className="bg-card rounded-lg p-4 md:p-6 glow-primary">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg md:text-xl font-heading font-bold text-foreground">
                    Atividades Recentes
                  </h3>
                  <button 
                    onClick={loadNotifications}
                    className="text-sm text-primary hover:text-primary/80 transition-smooth"
                  >
                    Atualizar
                  </button>
                </div>
                <div className="space-y-3">
                  {notifications?.length > 0 ? (
                    notifications?.slice(0, 10)?.map((notification) => (
                      <ActivityItem
                        key={notification?.id}
                        type={notification?.notificationType}
                        title={notification?.title}
                        description={notification?.description}
                        timestamp={notification?.createdAt}
                        status={notification?.status}
                      />
                    ))
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      Nenhuma atividade recente
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-6 md:mb-8">
              <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-4 md:mb-6">
                Desempenho por Categoria
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {categoryPerformance?.map((category, index) => (
                  <CategoryPerformanceCard
                    key={index}
                    name={category?.name}
                    itemCount={category?.itemCount}
                    views={category?.views}
                    rating={category?.rating}
                    trend={category?.trend}
                  />
                ))}
              </div>
            </div>

            <div className="mb-6 md:mb-8">
              <SystemSettingsCard 
                title="Configurações do Sistema"
                description="Gerencie as configurações principais do sistema"
                icon="Settings"
                iconColor="var(--color-primary)"
                settingType="general"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminDashboard;